import type { PrintTemplateView } from '#shared/types';
import type { PrintJobOptions } from '#client/lib/endpoints';

export interface EffectivePrintOptions {
  header: boolean;
  footer: boolean;
  watermark: boolean;
  signatures: boolean;
  history: boolean;
  pageSize: string;
  orientation: 'portrait' | 'landscape';
}

/** What the template says, or the built-in defaults while none has loaded. */
function templateAnswers(
  document: PrintTemplateView['document'] | undefined,
): Omit<EffectivePrintOptions, 'orientation'> & { orientation: string } {
  if (!document) {
    return {
      header: true,
      footer: true,
      watermark: true,
      signatures: true,
      // `false` unless somebody turned it on — a stored document is re-parsed on
      // the way out, so a default of true would add columns to every sheet the
      // lab has already approved.
      history: false,
      pageSize: 'A4',
      orientation: 'portrait',
    };
  }
  return {
    header: document.header.enabled,
    footer: document.footer.enabled,
    watermark: document.watermark.enabled,
    signatures: document.signatures.enabled,
    history: document.body.history.enabled,
    pageSize: document.page.size,
    orientation: document.page.orientation,
  };
}

/**
 * The effective value of each control: this job's override if there is one,
 * otherwise what the template says.
 */
export function effectivePrintOptions(
  options: PrintJobOptions,
  document: PrintTemplateView['document'] | undefined,
): EffectivePrintOptions {
  const template = templateAnswers(document);
  const orientation = options.orientation ?? template.orientation;
  return {
    header: options.header ?? template.header,
    footer: options.footer ?? template.footer,
    watermark: options.watermark ?? template.watermark,
    signatures: options.signatures ?? template.signatures,
    history: options.history ?? template.history,
    pageSize: options.page_size ?? template.pageSize,
    orientation: orientation === 'landscape' ? 'landscape' : 'portrait',
  };
}

/**
 * The report selection after ticking or unticking one report. `undefined`
 * means every report.
 *
 * Back to `undefined` when everything is ticked, so the URL carries no report
 * list and a report added in another tab is still picked up.
 */
export function toggleReportSelection(
  selected: readonly number[] | undefined,
  reportIds: readonly number[],
  change: { reportId: number; isIncluded: boolean },
): number[] | undefined {
  const current = selected ?? reportIds;
  const next = change.isIncluded
    ? [...current, change.reportId]
    : current.filter((id) => id !== change.reportId);
  return next.length === reportIds.length ? undefined : next;
}
